import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, KeyRound } from "lucide-react";

function emptyUser() { return { email: "", name: "", password: "", role: "agent", department: "Operaciones" }; }

export default function UsersPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyUser());
  const [resetUser, setResetUser] = useState(null);
  const [resetPwd, setResetPwd] = useState("");

  const fetchAll = async () => {
    const { data } = await api.get("/users");
    setItems(data);
  };

  useEffect(() => { fetchAll(); }, []);

  const onCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      await api.post("/users", payload);
      toast.success(form.password ? "Usuario creado" : "Usuario invitado · definirá su contraseña en el primer login");
      setOpen(false);
      setForm(emptyUser());
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const onDelete = async (u) => {
    if (!confirm(`¿Borrar usuario "${u.email}"?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Usuario eliminado");
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const onResetPassword = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/users/${resetUser.id}`, { password: resetPwd });
      toast.success(`Contraseña de ${resetUser.email} restablecida`);
      setResetUser(null);
      setResetPwd("");
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al restablecer la contraseña");
    }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <p className="vv-label">Administración</p>
          <h1 className="text-3xl font-semibold tracking-tight mt-1">Usuarios</h1>
          <p className="text-sm text-gray-500 mt-1">Administradores y agentes con acceso al sistema.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700" data-testid="create-user-button"><Plus size={16} className="mr-1"/> Nuevo usuario</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nuevo usuario</DialogTitle></DialogHeader>
            <form onSubmit={onCreate} className="space-y-4">
              <div>
                <Label className="vv-label">Nombre *</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input"/>
              </div>
              <div>
                <Label className="vv-label">Email *</Label>
                <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input"/>
              </div>
              <div>
                <Label className="vv-label">Contraseña inicial (opcional)</Label>
                <Input type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" placeholder="Déjalo en blanco para que la cree el usuario"/>
                <p className="text-[11px] text-gray-500 mt-1">Si lo dejas vacío, el usuario establecerá su contraseña la primera vez que entre con su email.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="vv-label">Rol</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="user-role-select"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="agent">Agente</SelectItem>
                      <SelectItem value="reconfirmador">Reconfirmador</SelectItem>
                      <SelectItem value="admin">Administrador</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="vv-label">Departamento</Label>
                  <Input value={form.department || ""} onChange={(e) => setForm({ ...form, department: e.target.value })}/>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="save-user-button">Crear</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="vv-card overflow-hidden">
        <table className="vv-table w-full">
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Email</th>
              <th>Rol</th>
              <th>Departamento</th>
              <th>Estado</th>
              <th>Alta</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.id}>
                <td className="font-medium">{u.name}</td>
                <td className="mono text-xs">{u.email}</td>
                <td>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${u.role === "admin" ? "bg-orange-50 text-orange-700" : "bg-gray-100 text-gray-700"}`}>{u.role}</span>
                </td>
                <td>{u.department || "—"}</td>
                <td>
                  {u.pending_first_login ? (
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-amber-50 text-amber-700">Pendiente primer login</span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-wider text-emerald-700">Activo</span>
                  )}
                </td>
                <td className="text-xs text-gray-500">{u.created_at?.slice(0, 10)}</td>
                <td className="text-right">
                  <button onClick={() => { setResetUser(u); setResetPwd(""); }} className="text-gray-400 hover:text-blue-600 mr-2" title="Restablecer contraseña" data-testid={`reset-password-button-${u.id}`}><KeyRound size={14}/></button>
                  <button onClick={() => onDelete(u)} className="text-gray-400 hover:text-red-600" title="Borrar"><Trash2 size={14}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Funciones avanzadas — Etapa 2 */}
      <Etapa2Card users={items} />

      {/* Modal restablecer contraseña */}
      <Dialog open={!!resetUser} onOpenChange={(v) => !v && setResetUser(null)}>
        <DialogContent data-testid="reset-password-modal">
          <DialogHeader><DialogTitle>Restablecer contraseña</DialogTitle></DialogHeader>
          <form onSubmit={onResetPassword} className="space-y-4">
            <p className="text-sm text-gray-600">
              Vas a establecer una nueva contraseña para <b>{resetUser?.email}</b>. El usuario podrá
              cambiarla después desde su perfil (icono de llave en la barra lateral).
            </p>
            <div>
              <Label className="vv-label">Nueva contraseña *</Label>
              <Input type="password" required minLength={6} value={resetPwd}
                onChange={(e) => setResetPwd(e.target.value)} data-testid="reset-password-input" autoFocus />
              <p className="text-[11px] text-gray-500 mt-1">Mínimo 6 caracteres.</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setResetUser(null)}>Cancelar</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="reset-password-submit">Restablecer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Tarjeta de administración del feature flag "Etapa 2" (bandeja de respuestas + IA).
 *  Permite activarla en este entorno y restringirla a usuarios concretos (p. ej. solo Heidi). */
function Etapa2Card({ users }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    api.get("/features").then(({ data }) => setCfg(data)).catch(() => {});
  }, []);

  if (!cfg) return null;
  const allowed = cfg.etapa2_allowed_emails || [];
  const eligible = users.filter((u) => u.role !== "reconfirmador");

  const toggleEnabled = (v) => { setCfg({ ...cfg, etapa2_enabled: v }); setDirty(true); };
  const toggleEmail = (email) => {
    const e = (email || "").toLowerCase();
    setCfg({
      ...cfg,
      etapa2_allowed_emails: allowed.includes(e) ? allowed.filter((x) => x !== e) : [...allowed, e],
    });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/features", { etapa2_enabled: cfg.etapa2_enabled, etapa2_allowed_emails: allowed });
      toast.success("Configuración de Etapa 2 guardada");
      setDirty(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="vv-card p-5 space-y-4" data-testid="etapa2-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="vv-label">Funciones avanzadas</p>
          <h2 className="text-lg font-semibold mt-0.5">Etapa 2 — Bandeja de respuestas e IA</h2>
          <p className="text-xs text-gray-500 mt-1 max-w-xl">
            Activa la lectura del buzón, el análisis de respuestas de proveedores con IA y las acciones
            sobre Sofi. Puedes limitar el acceso a usuarios concretos — el resto no verá nada nuevo.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${cfg.etapa2_enabled ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"}`} data-testid="etapa2-status-badge">
            {cfg.etapa2_enabled ? "Activada" : "Desactivada"}
          </span>
          <Switch checked={cfg.etapa2_enabled} onCheckedChange={toggleEnabled} data-testid="etapa2-global-switch" />
        </div>
      </div>

      {cfg.etapa2_enabled && (
        <div className="border-t border-gray-100 pt-3">
          <p className="text-xs font-medium text-gray-700 mb-1">¿Quién puede usar la Etapa 2?</p>
          <p className="text-[11px] text-gray-500 mb-2" data-testid="etapa2-allowed-hint">
            {allowed.length === 0
              ? "Sin restricción: todos los usuarios (excepto reconfirmadores) tienen acceso. Marca usuarios para restringirlo solo a ellos."
              : `Restringido a ${allowed.length} usuario${allowed.length > 1 ? "s" : ""}. Desmarca todos para abrirlo a todo el equipo.`}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {eligible.map((u) => (
              <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer rounded-md px-2 py-1.5 hover:bg-gray-50">
                <Checkbox
                  checked={allowed.includes((u.email || "").toLowerCase())}
                  onCheckedChange={() => toggleEmail(u.email)}
                  data-testid={`etapa2-user-checkbox-${u.id}`}
                />
                <span className="font-medium">{u.name}</span>
                <span className="text-xs text-gray-400 mono">{u.email}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-3">
        <Button onClick={save} disabled={!dirty || saving} size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="etapa2-save-button">
          {saving ? "Guardando…" : "Guardar cambios"}
        </Button>
        {dirty && <span className="text-[11px] text-amber-600">Cambios sin guardar</span>}
      </div>
    </div>
  );
}
